import Icon from '@components/v2/ui/Icon/Icon';
import classNames from 'classnames';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef } from 'react';
import {
  PRODUCT_STOCK_OVERHANG,
  TOUCH_PRODUCT_STOCK_OVERHANG,
} from '../../../../../../../../components/Product/useProductQuantityInputStyles';
import useArrowScroller from '../../../../../../../../components/Scroller/hooks/useArrowScroller';
import { ScrollDirection } from '../../../../../../../../components/Scroller/types/arrowScroller.types';
import LangEnum from '../../../../../../../../lang/LangEnum';
import useTeceoStyles from '../../../../../../../../useTeceoStyles';
import { GRID_ITEM_WIDTH } from '../../../../../../../../utils/configureGridItemsWidth';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { useOrderProductsLayoutContext } from '../../../../../../contexts/OrderProductsLayout.context';
import { getGridSpacing, useHorizontalScrollStyles } from './HorizontalScroll.styles';

export interface HorizontalScrollProps {
  highlightProduct?: boolean;
  children: React.ReactNode;
  ExtraButton?: React.ReactNode;
}

const SCROLL_COLUMNS = 3;

const HorizontalScroll = ({ highlightProduct, children, ExtraButton }: HorizontalScrollProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { translate } = useTranslation();
  const { hasTouchGrid } = useOrderProductsLayoutContext();

  const { canScrollLeft, canScrollRight, scroll } = useArrowScroller({
    containerRef,
    scrollAmount: (GRID_ITEM_WIDTH + getGridSpacing(hasTouchGrid)) * SCROLL_COLUMNS,
  });

  const hasExtraButton = !!ExtraButton;
  const stockOverhang = Math.ceil(
    hasTouchGrid ? TOUCH_PRODUCT_STOCK_OVERHANG : PRODUCT_STOCK_OVERHANG,
  );

  const { classes: teceoClasses } = useTeceoStyles();
  const { classes: horizontalScrollStyles } = useHorizontalScrollStyles({
    canScrollRight,
    canScrollLeft,
    hasExtraButton,
    hasTouchGrid,
    highlightProduct: !!highlightProduct,
  });
  return (
    <div
      ref={containerRef}
      style={{
        overflowY: 'hidden',
        display: 'flex',
        paddingBottom: stockOverhang,
        alignItems: hasExtraButton ? 'initial' : 'center',
        position: 'relative',
      }}
      className={teceoClasses.hideScrollbar}
    >
      {canScrollLeft && (
        <div
          className={classNames(
            'shadowContainer',
            'scrollFade',
            horizontalScrollStyles.stickyContent,
            horizontalScrollStyles.scrollContainerLeft,
            horizontalScrollStyles.arrowContainerLeft,
          )}
        >
          <button
            type="button"
            className={horizontalScrollStyles.arrow}
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              border: 'none',
              padding: 0,
              background: 'inherit',
            }}
            aria-label={translate(LangEnum.GLOBAL_PREVIOUS)}
            onClick={(event) => {
              event.stopPropagation();
              scroll(ScrollDirection.LEFT);
            }}
          >
            <Icon icon={ChevronLeft} size="xs" />
          </button>
        </div>
      )}

      {children}

      <div
        className={classNames(
          'shadowContainer',
          canScrollRight && 'scrollFade',
          horizontalScrollStyles.stickyContent,
          horizontalScrollStyles.scrollContainerRight,
          horizontalScrollStyles.actionsContainerRight,
        )}
      >
        {canScrollRight && (
          <button
            type="button"
            className={horizontalScrollStyles.arrow}
            style={{
              position: 'absolute',
              right: '100%',
              bottom: 0,
              border: 'none',
              padding: 0,
              background: 'inherit',
            }}
            aria-label={translate(LangEnum.GLOBAL_NEXT)}
            onClick={(event) => {
              event.stopPropagation();
              scroll(ScrollDirection.RIGHT);
            }}
          >
            <Icon icon={ChevronRight} size="xs" />
          </button>
        )}
        {ExtraButton}
      </div>
    </div>
  );
};

export default HorizontalScroll;
